import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementSystem,
  GeckoMeasurementUnit,
} from 'types'

const ComponentsWithWalls = [GeckoComponentModelType.LegacyBoilerWalls]

const ComponentsWithCourses = [
  GeckoComponentModelType.LegacyCone,
  GeckoComponentModelType.VerticalTankShell,
  GeckoComponentModelType.Cone,
]

const ComponentsWithBaseNominalThickness = [
  GeckoComponentModelType.TankFloor,
  GeckoComponentModelType.TankFlatRoof,
]

const ComponentsWithWallNominalThickness = [
  GeckoComponentModelType.PipeSection,
  GeckoComponentModelType.HorizontalTankShell,
]

const minReducer = (
  min: GeckoMeasurement | undefined,
  thickness: GeckoMeasurement | undefined
) => {
  return thickness && min
    ? GeckoMeasurement.Min(min, thickness)
    : min
      ? min
      : thickness
}

const maxReducer = (
  max: GeckoMeasurement | undefined,
  thickness: GeckoMeasurement | undefined
) => {
  return thickness && max
    ? GeckoMeasurement.Max(max, thickness)
    : max
      ? max
      : thickness
}

export const getNominalThicknessForComponent = (
  assetModelComponent: GeckoComponentModel,
  type: 'min' | 'max'
) => {
  if (
    'walls' in assetModelComponent &&
    ComponentsWithWalls.includes(assetModelComponent.type)
  ) {
    const walls = assetModelComponent.walls
    if (!walls) {
      return undefined
    }

    const wallNominalThicknesses = [...walls.values()]
      .filter((wall) => wall.wallNominalThickness)
      .map((wall) => wall.wallNominalThickness)
      .reduce(type === 'min' ? minReducer : maxReducer, undefined)

    return wallNominalThicknesses
  }

  if (
    'courses' in assetModelComponent &&
    ComponentsWithCourses.includes(assetModelComponent.type)
  ) {
    const courses = assetModelComponent.courses
    if (!courses) {
      return undefined
    }

    const courseNominalThicknesses = [...courses.values()]
      .filter((course) => course.wallNominalThickness)
      .map((course) => course.wallNominalThickness)
      .reduce(
        (acc, thickness) =>
          (type === 'min' ? minReducer : maxReducer)(acc, thickness),
        undefined
      )

    return courseNominalThicknesses
  }

  if (
    'baseNominalThickness' in assetModelComponent &&
    ComponentsWithBaseNominalThickness.includes(assetModelComponent.type)
  ) {
    return assetModelComponent.baseNominalThickness
  }

  if (
    'wallNominalThickness' in assetModelComponent &&
    ComponentsWithWallNominalThickness.includes(assetModelComponent.type)
  ) {
    return assetModelComponent.wallNominalThickness
  }

  return undefined
}

export const getNominalThicknessStringForComponent = (
  assetModelComponent: GeckoComponentModel | undefined,
  type: 'min' | 'max',
  measurementSystem: GeckoMeasurementSystem
) => {
  if (!assetModelComponent) {
    return undefined
  }

  const nominalThickness = getNominalThicknessForComponent(
    assetModelComponent,
    type
  )
  if (!nominalThickness) {
    return undefined
  }

  return nominalThickness
    .convertTo(
      measurementSystem === 'imperial'
        ? GeckoMeasurementUnit.Inches
        : GeckoMeasurementUnit.Millimeters
    )
    .toString()
}
